import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { API } from '../../../shared/constants/constants';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import { useState } from 'react';
import PropTypes from 'prop-types';
import { getOrganizationsByCaseInsensitiveName, getOrgsByCaseInsensitiveName } from '../contactsRequest';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import {
  normalizeOrganizationPayload,
  normalizeOrganizationQueryName,
} from '../../../shared/requests/organizationNormalization';
import { appPalette } from '../../../theme/palette';
import {
  queryAdderPopupFieldLabelSx,
  queryAdderPopupInputSx,
  utilityCancelButtonSx,
  utilityModalContentSx,
  utilityModalEyebrowSx,
  utilityModalFooterSx,
  utilityModalHeaderSx,
  utilityModalIconTileSx,
  utilityModalPaperSx,
  utilityModalTitleSx,
  utilityPrimaryActionButtonSx,
} from '../../QueryAdder/queryAdderPopupStyles';

const ContactsPageAddOutletModal = (props) => {
  const { openModal, setOpenModal, setRefreshContacts, category } = props;
  const [textFieldValue, setTextFieldValue] = useState('');

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const duplicatesQuery = useQuery({
    queryKey: ['orgDuplicates', category, textFieldValue],
    queryFn: () => {
      if (category === 'Outlets') {
        return getOrgsByCaseInsensitiveName(textFieldValue);
      }
      return getOrganizationsByCaseInsensitiveName(normalizeOrganizationQueryName(textFieldValue));
    },
    enabled: false,
    staleTime: 0,
  });

  const createOrgMutation = useMutation({
    mutationFn: (newOrg) => {
      if (category === 'Outlets') {
        const normalizedOrg = { name: newOrg?.name };
        return API.post('/org', normalizedOrg);
      }
      return API.post('/organization', normalizeOrganizationPayload(newOrg));
    },
    onSuccess: async (response) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(
        openSnackbar({
          severity: 'success',
          message: `${category.slice(0, -1)}: ${textFieldValue} created!`,
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      setRefreshContacts((prev) => !prev);
      handleClose();
    },
    onError: (err) => {
      alert(`post failed - ${err}`);
    },
  });

  const handleClose = () => {
    setOpenModal(false);
  };

  const countDuplicates = async () => {
    try {
      const { data } = await duplicatesQuery.refetch();
      const responseData = Array.isArray(data?.data) ? data.data : [];
      return responseData.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const duplicates = await countDuplicates();

    if (duplicates > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${duplicates} ${category
            .toLowerCase()
            .slice(0, -1)}(s) with the same name already exists. Please check if you are trying to add an ${category
            .toLowerCase()
            .slice(0, -1)} that already exists.`,
        })
      );
      return;
    } else {
      await createOrgMutation.mutateAsync({ name: textFieldValue });
      event.stopPropagation();
    }
  };

  return (
    <Dialog
      open={openModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          ...utilityModalPaperSx,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={utilityModalHeaderSx}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={utilityModalIconTileSx}>
              <AddBusinessIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={utilityModalEyebrowSx}>CONTACTS</Typography>
              <Typography sx={utilityModalTitleSx}>Add New {category.slice(0, -1)}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={utilityModalContentSx}>
          <Typography sx={queryAdderPopupFieldLabelSx}>
            Name{' '}
            <Box component="span" sx={{ color: appPalette.primary.main }}>
              *
            </Box>
          </Typography>
          <TextField
            required
            autoFocus
            fullWidth
            value={textFieldValue}
            onChange={(event) => {
              setTextFieldValue(event.target.value);
            }}
            id="name"
            placeholder="Add a name"
            type="text"
            variant="outlined"
            sx={queryAdderPopupInputSx}
            data-cy="name-input-ContactsPageAddOutletModal"
          />
        </DialogContent>
        <DialogActions sx={{ ...utilityModalFooterSx, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={utilityCancelButtonSx}
            data-cy="cancel-button-ContactsPageAddOutletModal"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={utilityPrimaryActionButtonSx}
            data-cy="add-button-ContactsPageAddOutletModal"
          >
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ContactsPageAddOutletModal.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  setRefreshContacts: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired,
};

export default ContactsPageAddOutletModal;
